import firebase from '../../firebase/compat'
import Tooltip from '../../components/common/Tooltip'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined'
import { useFirestoreGeneralOrderBy } from '../../firebase/useFirestore'
import { db } from '../../firebase/config'
import { v4 as uuid } from 'uuid'
import { client } from '../../hooks/Client'
import { SavedIcon } from '../../StateManagment/SavedIcon'
import Location from '../../hooks/Location'
import { useContext, useState, useEffect } from 'react'
import DeleteModal from '../../components/common/DeleteModal'
import Table from '../../components/common/table/Table.jsx'
import TableCell from '../../components/common/table/TableCell.jsx'
import TableTitleCell from '../../components/common/table/TableTitleCell.jsx'
import TableDragHandleCell from '../../components/common/table/TableDragHandleCell.jsx'
import ReadOnlyGuard from '../../components/common/ReadOnlyGuard'
import softDeleteDoc from '../../helpers/softDeleteDoc'
import { Auth } from '../../StateManagment/Auth'
import { goalEffectIds, goalTitle } from '../../helpers/goalsHelpers/personaGoalFields'
import useEffectNameById from '../../hooks/personas/useEffectNameById'

const Goals = ({ personaNavigation }) => {
    // Context
    const [saved, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)

    // Hooks
    const personaId = Location()[3]

    // Firestore
    const goals = useFirestoreGeneralOrderBy('PersonaGoals', 'PersonaID', personaId ? personaId : '', 'Position', 'asc')
    const effectNameById = useEffectNameById()

    // Doelen staan in drie opslagvormen; de helper kent ze alle drie.
    const titleOf = (goal) => goalTitle(goal, effectNameById || {})

    // State
    const [dragArray, setDragArray] = useState([])
    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteName, setDeleteName] = useState('dit doel')
    const [deleteDocid, setDeleteDocid] = useState('')

    // Skip state update when snapshot is structurally identical — avoids
    // re-rendering the DragDropContext on every unrelated Firestore write,
    // which would cause react-beautiful-dnd to remeasure and jump the page.
    useEffect(() => {
        setDragArray(prev => {
            const next = goals || []
            if (Array.isArray(prev) && prev.length === next.length) {
                const same = prev.every((p, i) => {
                    const n = next[i]
                    return (
                        p.docid === n.docid &&
                        p.Title === n.Title &&
                        p.Status === n.Status &&
                        p.Position === n.Position &&
                        JSON.stringify(p.EffectIDs || []) === JSON.stringify(n.EffectIDs || [])
                    )
                })
                if (same) return prev
            }
            return next
        })
    }, [goals])

    // Drag & drop
    const handleDragEnd = (result) => {
        if (!result.destination) return

        const items = Array.from(dragArray)
        const [moved] = items.splice(result.source.index, 1)
        items.splice(result.destination.index, 0, moved)

        setDragArray(items)

        const batch = db.batch()
        items.forEach((goal, idx) => {
            batch.update(db.collection('PersonaGoals').doc(goal.docid), { Position: idx + 1 })
        })
        batch.commit().then(() => setSaved('flex'))
    }

    // Add goal
    const addGoal = async () => {
        const position = (goals ? goals.length : 0) + 1

        await db.collection('PersonaGoals').add({
            ID: uuid(),
            PersonaID: personaId,
            Title: 'Nieuw doel ' + position,
            Description: '',
            EffectIDs: [],
            Status: 'niet-bereikt',
            Position: position,
            CompagnyID: client,
            Compagny: client,
            Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
        })
    }

    // Status change from table
    const statusHandler = (e) => {
        const docid = e.target.dataset.docid
        db.collection('PersonaGoals').doc(docid).update({ Status: e.target.value })
            .then(() => setSaved('flex'))
    }

    // Delete modal
    const deleteModalHandler = (e) => {
        const docid = e.currentTarget.dataset.docid
        const name = e.currentTarget.dataset.deletename
        setDeleteDocid(docid)
        setDeleteName(name || 'dit doel')
        setDeleteModal(!deleteModal)
    }

    // Zacht verwijderd (ADR-008): terug te zetten via de prullenbak.
    const deleteGoal = () => {
        softDeleteDoc({ collection: 'PersonaGoals', docid: deleteDocid, authId: auth?.ID, title: deleteName })
            .then(() => setSaved('flex'))
    }

    return (
        <div style={{ display: personaNavigation === 'goals' ? 'block' : 'none' }}>
            <div className="persona-option-container">
                <div className="table-container">
                    <ReadOnlyGuard edit="personas.edit">
                        {(canEdit) => (
                            <Table
                                columns={[
                                    { key: 'drag', label: '', searchable: false },
                                    { key: 'title', label: 'TITEL' },
                                    { key: 'status', label: 'STATUS', searchable: false },
                                    { key: 'effects', label: 'EFFECTEN', searchable: false },
                                    { key: 'delete', label: 'VERWIJDEREN', searchable: false },
                                ]}
                                items={dragArray || []}
                                getItemKey={(goal) => goal.ID}
                                renderRow={(goal) => (
                                    <>
                                        <TableDragHandleCell>
                                            <DragIndicatorOutlinedIcon />
                                        </TableDragHandleCell>
                                        <TableTitleCell
                                            value={titleOf(goal)}
                                            to={`/${client}/personagoaldetail/${personaId}/${goal.ID}`}
                                            readOnly
                                        />
                                        <TableCell>
                                            <select
                                                data-docid={goal.docid}
                                                value={goal.Status || 'niet-bereikt'}
                                                onChange={statusHandler}
                                            >
                                                <option value="niet-bereikt">Niet bereikt</option>
                                                <option value="bereikt">Bereikt</option>
                                            </select>
                                        </TableCell>
                                        <TableCell>
                                            {goalEffectIds(goal).length}
                                        </TableCell>
                                        <TableCell>
                                            {canEdit && (
                                                <Tooltip content={'Doel verwijderen'} top='-60px' width={'30px'}>
                                                    <DeleteOutlinedIcon
                                                        className='table-delete-icon'
                                                        data-docid={goal.docid}
                                                        data-deletename={titleOf(goal)}
                                                        onClick={deleteModalHandler}
                                                    />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </>
                                )}
                                searchAccessor={titleOf}
                                searchPlaceholder="Zoeken op titel"
                                emptyMessage="Geen doelen gevonden."
                                draggable
                                droppableId="goals"
                                onDragEnd={handleDragEnd}
                                addRow={canEdit ? {
                                    toolTipContent: 'Doel toevoegen',
                                    onClick: addGoal,
                                } : undefined}
                            />
                        )}
                    </ReadOnlyGuard>
                    <DeleteModal
                        deleteModal={deleteModal}
                        setDeleteModal={setDeleteModal}
                        deleteItem={deleteGoal}
                        deleteName={deleteName}
                        softDelete
                    />
                </div>
            </div>
        </div>
    )
}

export default Goals
